import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useUpdateProfile } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { getGetMeQueryKey } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { Check, Mail, Shield, ArrowLeft } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";

export default function EditProfile() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { mutateAsync: updateProfile, isPending } = useUpdateProfile();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const u = user as (typeof user & { avatarUrl?: string | null; bio?: string | null; phone?: string | null }) | null;

  const [name, setName] = useState(u?.name ?? "");
  const [avatarUrl, setAvatarUrl] = useState(u?.avatarUrl ?? "");
  const [avatarImgError, setAvatarImgError] = useState(false);
  const [bio, setBio] = useState(u?.bio ?? "");
  const [phone, setPhone] = useState(u?.phone ?? "");
  const [email, setEmail] = useState(user?.email ?? "");

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <p className="text-slate-500">You need to be signed in to edit your profile.</p>
          <button
            onClick={() => setLocation("/login")}
            className="text-primary font-semibold hover:underline"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const initials = name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({ title: "Name required", description: "Please enter your name.", variant: "destructive" });
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail.includes("@") || !trimmedEmail.includes(".")) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    try {
      await updateProfile({
        data: {
          name: name.trim(),
          avatarUrl: avatarUrl || undefined,
          bio: bio.trim() || undefined,
          phone: phone.trim() || undefined,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(trimmedEmail !== user.email ? { email: trimmedEmail } : {}),
        } as Parameters<typeof updateProfile>[0]["data"],
      });
      await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      toast({ title: "Saved!", description: "Your profile has been updated." });
      setLocation("/account");
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast({ title: "Email taken", description: "That email address is already in use.", variant: "destructive" });
      } else if (status === 400) {
        toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      } else {
        toast({ title: "Error", description: "Could not save your profile. Please try again.", variant: "destructive" });
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-primary px-4 py-6 flex items-center gap-4">
        <button
          onClick={() => setLocation("/account")}
          className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-white font-bold text-xl">Edit Profile</h1>
          <p className="text-white/70 text-sm">Update your name, photo, and bio</p>
        </div>
      </div>

      <div className="flex flex-col items-center bg-primary px-4 pb-6">
        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white/20 flex items-center justify-center flex-shrink-0 mb-2">
          {avatarUrl && !avatarImgError ? (
            <img src={avatarUrl} alt={name} className="w-full h-full object-cover" onError={() => setAvatarImgError(true)} />
          ) : (
            <span className="text-white font-bold text-2xl">{initials}</span>
          )}
        </div>
        <p className="text-white/70 text-xs">{avatarUrl ? "Photo uploaded" : "Using initials — upload a photo below"}</p>
      </div>

      <div className="max-w-xl mx-auto px-4 py-6 space-y-5">
        <form onSubmit={handleSave} className="space-y-5">
          <div className="space-y-2">
            <p className="text-sm font-bold text-slate-700">Personal Details</p>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <FormField id="ep-name" label="Full Name *" value={name} onChange={setName} placeholder="Your name" />
              <div className="h-px bg-slate-100 ml-4" />
              <div className="px-4 py-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Profile Photo</p>
                <ImageUpload value={avatarUrl} onChange={(url) => { setAvatarUrl(url); setAvatarImgError(false); }} label="Upload Photo" />
              </div>
              <div className="h-px bg-slate-100 ml-4" />
              <FormField id="ep-bio" label="Bio" value={bio} onChange={setBio} placeholder="A short description about you..." multiline />
              <div className="h-px bg-slate-100 ml-4" />
              <FormField id="ep-phone" label="Phone Number" value={phone} onChange={setPhone} placeholder="07700 900 000" type="tel" />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-bold text-slate-700">Login &amp; Account</p>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3">
                <label htmlFor="ep-email" className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
                  <Mail size={10} className="inline mr-1 mb-0.5" />Email
                </label>
                <input
                  id="ep-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none bg-transparent"
                />
              </div>
              <p className="text-[11px] text-slate-400 px-4 pb-3 -mt-1">Changing your email will update your login credentials.</p>
              <div className="h-px bg-slate-100" />
              <div className="flex items-center gap-3 px-4 py-3.5">
                <Shield size={14} className="text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-500 flex-1">Account type</span>
                <span className="text-sm font-medium text-slate-700">
                  {user.role === "business" ? "Business Owner" : user.role === "tradesperson" ? "Tradesperson" : "Resident"}
                </span>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isPending}
            className="w-full flex items-center justify-center gap-2.5 bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-opacity disabled:opacity-70"
          >
            {isPending ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <><Check size={17} /> Save Profile</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function FormField({
  id, label, value, onChange, placeholder, multiline, type,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  type?: string;
}) {
  return (
    <div className="px-4 py-3">
      <label htmlFor={id} className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">{label}</label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none resize-none bg-transparent leading-relaxed"
        />
      ) : (
        <input
          id={id}
          type={type ?? "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none bg-transparent"
        />
      )}
    </div>
  );
}
